import { memo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { SiteDecorativeBackground, SitePageContainer, siteUi } from "./site-igreja-ui";
import { cn } from "@/lib/utils";
import type { SitePageKey } from "@/interfaces/site-section.interface";

function SiteIgrejaNavSkeleton() {
  return (
    <header className="fixed top-3 z-50 w-full px-3 md:top-4 md:px-4">
      <div
        className={cn(
          siteUi.nav,
          "mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:h-16 sm:px-6",
        )}
      >
        <div className="flex items-center gap-2.5">
          <Skeleton variant="shimmer" className="h-9 w-9 rounded-full" />
          <Skeleton variant="shimmer" className="h-6 w-28 rounded-lg sm:w-36" />
        </div>
        <div className="hidden items-center gap-2 lg:flex">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="shimmer" className="h-8 w-16 rounded-full" />
          ))}
        </div>
        <Skeleton variant="shimmer" className="h-9 w-9 rounded-full lg:hidden" />
      </div>
    </header>
  );
}

export const SiteIgrejaHeroSkeleton = memo(function SiteIgrejaHeroSkeleton() {
  return (
    <section className="relative mb-8 min-h-[72vh] overflow-hidden rounded-b-[2rem] md:mx-4 md:min-h-[76vh] md:rounded-b-[2.5rem]">
      <Skeleton variant="shimmer" className="absolute inset-0 min-h-[72vh] md:min-h-[76vh]" />
      <div className="relative z-10 flex min-h-[72vh] flex-col justify-end px-6 pb-28 pt-24 sm:px-10 md:min-h-[76vh]">
        <Skeleton variant="shimmer" className="h-12 w-3/4 max-w-xl rounded-xl sm:h-14" />
        <Skeleton variant="shimmer" className="mt-4 h-6 w-full max-w-lg rounded-lg" />
        <Skeleton variant="shimmer" className="mt-2 h-6 w-2/3 max-w-md rounded-lg" />
      </div>
      <div className="absolute bottom-3 left-0 right-0 z-20 px-3 sm:bottom-4 md:bottom-5 md:px-4">
        <div className={cn(siteUi.nav, "mx-auto max-w-7xl")}>
          <div className="flex flex-col gap-3 px-4 py-3.5 sm:h-14 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6 sm:py-0 md:h-16">
            <Skeleton variant="shimmer" className="h-5 w-full max-w-xs rounded-md" />
            <Skeleton variant="shimmer" className="h-5 w-full max-w-xs rounded-md" />
          </div>
        </div>
      </div>
    </section>
  );
});

/** Réplica do cartão real (`SiteEventoCard`/`SiteCursoCard`): capa quadrada com o
 * badge de data no canto, título e uma linha de meta abaixo — não uma caixa cinza
 * genérica, pra não trocar de forma quando o conteúdo real chega. */
function SiteCardSkeleton({ showBadge = true }: { showBadge?: boolean }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="relative aspect-square overflow-hidden rounded-2xl">
        <Skeleton variant="shimmer" className="absolute inset-0 rounded-2xl" />
        {showBadge ? (
          <div className="absolute left-2.5 top-2.5 h-9 w-11 rounded-xl bg-white/70 sm:left-3 sm:top-3" />
        ) : null}
      </div>
      <Skeleton variant="shimmer" className="h-4 w-11/12 rounded-md" />
      <Skeleton variant="shimmer" className="h-3 w-2/3 rounded-md" />
    </div>
  );
}

/** Mesma grade responsiva de `SiteEventosGrid`/`SiteCursosGrid` (2/3/4 colunas). */
function SiteCardGridSkeleton({
  count,
  title,
  showBadge = true,
}: {
  count: number;
  title?: boolean;
  showBadge?: boolean;
}) {
  return (
    <div className="space-y-6 md:space-y-8">
      {title ? (
        <div className="space-y-2">
          <Skeleton variant="shimmer" className="h-7 w-48 rounded-lg" />
          <Skeleton variant="shimmer" className="h-4 w-64 max-w-full rounded-md" />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3 md:gap-6 xl:grid-cols-4">
        {Array.from({ length: count }).map((_, i) => (
          <SiteCardSkeleton key={i} showBadge={showBadge} />
        ))}
      </div>
    </div>
  );
}

/** Barra de busca real é `h-12 rounded-full pl-12` (ver `Eventos.tsx`/`Grupos.tsx`/
 * `Ensino.tsx`) — pílula, não retângulo. */
function SiteSearchBarSkeleton() {
  return <Skeleton variant="shimmer" className="mb-8 h-12 w-full max-w-xl rounded-full" />;
}

/** Páginas de listagem (Eventos, Pequenos Grupos, Ensino): busca + grade de cartões. */
export const SiteIgrejaListPageSkeleton = memo(function SiteIgrejaListPageSkeleton() {
  return (
    <SitePageContainer>
      <SiteSearchBarSkeleton />
      <SiteCardGridSkeleton count={8} />
    </SitePageContainer>
  );
});

/** Linha de atalhos da home — mesma forma do `SiteQuickAccessCard` real (cartão glass,
 * ícone 16×16 arredondado, título curto, descrição de uma linha). */
function SiteQuickAccessRowSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className={cn(siteUi.glass, "flex flex-col items-center p-6 text-center")}>
          <Skeleton variant="shimmer" className="mb-4 h-16 w-16 rounded-2xl" />
          <Skeleton variant="shimmer" className="h-5 w-20 rounded-md" />
          <Skeleton variant="shimmer" className="mt-2 h-3 w-28 rounded-md" />
        </div>
      ))}
    </div>
  );
}

/** Faixa de módulos (Grupos · Oração · Doações) — três blocos lado a lado. */
function SiteModulosRowSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} variant="shimmer" className="h-24 rounded-2xl" />
      ))}
    </div>
  );
}

/**
 * Corpo da home: atalhos → módulos → próximos eventos → artigos. É uma aproximação (as
 * seções reais são configuráveis pelo construtor visual, então a ordem/composição exata só
 * se sabe depois que `publico` carrega) — mas usa as MESMAS formas de cartão da grade de
 * eventos/artigos reais, então não troca de forma quando o conteúdo chega.
 */
function SiteIgrejaHomeBodySkeleton() {
  return (
    <SitePageContainer className="space-y-14 py-10 md:space-y-16 md:py-12">
      <SiteQuickAccessRowSkeleton />
      <SiteModulosRowSkeleton />
      <SiteCardGridSkeleton count={4} title />
      <SiteCardGridSkeleton count={3} title showBadge={false} />
    </SitePageContainer>
  );
}

/** Bloco de conteúdo genérico (título + parágrafo + CTA) — usado onde a página real é
 * texto/CTA do construtor visual, não uma lista de cartões nem um formulário de fato
 * (ex.: Cadastro de Membros, que aqui é uma landing de seções, não um form). */
export function SiteIgrejaContentBlockSkeleton() {
  return (
    <SitePageContainer className="max-w-lg space-y-4 py-10 md:py-12">
      <Skeleton variant="shimmer" className="h-8 w-2/3 rounded-lg" />
      <Skeleton variant="shimmer" className="h-4 w-full rounded-md" />
      <Skeleton variant="shimmer" className="h-4 w-5/6 rounded-md" />
      <Skeleton variant="shimmer" className="mt-4 h-12 w-48 rounded-full" />
    </SitePageContainer>
  );
}

/**
 * Skeleton de boot do Site da Igreja — nav, hero e atalhos rápidos. Sem `page` (primeiro
 * paint, antes de sabermos a rota) cai no bloco genérico.
 */
export const SiteIgrejaBootSkeleton = memo(function SiteIgrejaBootSkeleton() {
  return (
    <div className={siteUi.page}>
      <SiteDecorativeBackground />
      <SiteIgrejaNavSkeleton />
      <main className="relative z-10 pb-16">
        <SiteIgrejaHomeBodySkeleton />
      </main>
      <footer className="relative z-10 border-t border-white/50 bg-white/70 backdrop-blur-lg">
        <div className="container mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:grid-cols-2 md:px-6 lg:grid-cols-3">
          <Skeleton variant="shimmer" className="h-24 w-full rounded-xl" />
          <Skeleton variant="shimmer" className="h-24 w-full rounded-xl" />
          <Skeleton variant="shimmer" className="hidden h-24 w-full rounded-xl lg:block" />
        </div>
      </footer>
    </div>
  );
});

/** Skeleton só da área principal (nav real já visível no shell) — escolhe a forma pela
 * página de destino em vez de sempre mostrar a mesma grade de ícones da home. */
export const SiteIgrejaPageSkeleton = memo(function SiteIgrejaPageSkeleton({
  page,
}: {
  page?: SitePageKey | null;
}) {
  switch (page) {
    case "home":
      return <SiteIgrejaHomeBodySkeleton />;
    case "eventos":
    case "grupos":
    case "ensino":
      return <SiteIgrejaListPageSkeleton />;
    case "comunidade":
      return (
        <SitePageContainer className="py-10 md:py-12">
          <SiteCardGridSkeleton count={6} showBadge={false} />
        </SitePageContainer>
      );
    case "cadastro-membros":
      return <SiteIgrejaContentBlockSkeleton />;
    default:
      return <SiteIgrejaContentBlockSkeleton />;
  }
});

/** Réplica de uma linha de "Seções da página" na sidebar do construtor (ver
 * `SiteBuilderSidebar.tsx`) — usada enquanto o rascunho ainda não hidratou. */
export const SiteBuilderSectionListSkeleton = memo(function SiteBuilderSectionListSkeleton() {
  return (
    <div className="flex flex-col gap-1.5">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} variant="shimmer" className="h-9 w-full rounded-lg" />
      ))}
    </div>
  );
});

/**
 * Canvas do construtor enquanto o rascunho da página ainda não hidratou (ver
 * `useHydrateSectionsOnce` — só depois disso `sections` deixa de estar vazio). Réplica a
 * hero + 2 blocos de seção genéricos, no mesmo cartão emoldurado do canvas real.
 */
export const SiteBuilderCanvasSkeleton = memo(function SiteBuilderCanvasSkeleton() {
  return (
    <>
      <SiteIgrejaHeroSkeleton />
      <SitePageContainer className="space-y-6 pb-10">
        <Skeleton variant="shimmer" className="h-40 w-full rounded-[2rem]" />
        <Skeleton variant="shimmer" className="h-40 w-full rounded-[2rem]" />
      </SitePageContainer>
    </>
  );
});
